import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PersonFinal } from '@/api/entities';
import { useUser } from '@/contexts/UserContext';
import { Search, Trash2, AlertTriangle, UserX } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function PersonaDeletionManager() {
  const contextUser = useUser();
  const currentUser = contextUser;
  const [searchTerm, setSearchTerm] = useState('');
  const [personas, setPersonas] = useState([]);
  const [filteredPersonas, setFilteredPersonas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    if (contextUser && contextUser.role !== 'admin') {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: "You must be a super admin to access this feature."
      });
    }
  }, [contextUser]);

  const handleSearch = async () => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      toast({
        variant: "destructive",
        title: "Invalid Search",
        description: "Please enter at least 2 characters to search."
      });
      return;
    }

    setIsSearching(true);
    try {
      const allPersonas = await PersonFinal.list();
      
      const searchLower = searchTerm.toLowerCase().trim();
      
      const filtered = allPersonas.filter(person => {
        const firstNameMatch = person.first_name?.toLowerCase().includes(searchLower);
        const lastNameMatch = person.last_name?.toLowerCase().includes(searchLower);
        const fullNameMatch = `${person.first_name} ${person.last_name}`.toLowerCase().includes(searchLower);
        const roleTitleMatch = person.role_title?.toLowerCase().includes(searchLower);
        
        return firstNameMatch || lastNameMatch || fullNameMatch || roleTitleMatch;
      });

      setFilteredPersonas(filtered);
      
      if (filtered.length === 0) {
        toast({
          title: "No Results",
          description: "No personas found matching your search."
        });
      } else {
        toast({
          title: "Search Complete",
          description: `Found ${filtered.length} matching persona${filtered.length === 1 ? '' : 's'}.`
        });
      }
    } catch (error) {
      console.error('Error searching personas:', error);
      toast({
        variant: "destructive",
        title: "Search Failed",
        description: "Failed to search personas. Please try again."
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setIsLoading(true);
    try {
      await PersonFinal.delete(deleteTarget.id);
      
      setFilteredPersonas(prev => prev.filter(p => p.id !== deleteTarget.id));
      
      toast({
        title: "Persona Deleted",
        description: `Successfully deleted ${deleteTarget.first_name} ${deleteTarget.last_name}.`
      });
      
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting persona:', error);
      toast({
        variant: "destructive",
        title: "Delete Failed",
        description: "Failed to delete persona. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (currentUser.role !== 'admin') {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600">You must be a super admin to access this feature.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserX className="w-5 h-5" />
          Persona Search & Delete
        </CardTitle>
        <p className="text-sm text-gray-600 mt-2">
          Search for personas by name or role title, then delete records as needed. This action cannot be undone.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              placeholder="Search by name or role title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
            />
          </div>
          <Button 
            onClick={handleSearch}
            disabled={isSearching || !searchTerm || searchTerm.trim().length < 2}
          >
            <Search className="w-4 h-4 mr-2" />
            {isSearching ? 'Searching...' : 'Search'}
          </Button>
        </div>

        {filteredPersonas.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">
                Search Results ({filteredPersonas.length})
              </h3>
              <Badge variant="secondary">
                {filteredPersonas.length} persona{filteredPersonas.length === 1 ? '' : 's'}
              </Badge>
            </div>

            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredPersonas.map((persona) => (
                <div
                  key={persona.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900">
                          {persona.first_name} {persona.last_name}
                        </h4>
                        {persona.tenant_id && (
                          <Badge variant="outline" className="text-xs">
                            {persona.tenant_id}
                          </Badge>
                        )}
                      </div>
                      
                      {persona.role_title && (
                        <p className="text-sm text-gray-600 mb-1">{persona.role_title}</p>
                      )}
                      
                      {persona.account_name && (
                        <p className="text-sm text-gray-500">
                          <span className="font-medium">Account:</span> {persona.account_name}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap gap-2 mt-2">
                        {persona.email && (
                          <span className="text-xs text-gray-500">{persona.email}</span>
                        )}
                        {persona.persona_fit_score != null && (
                          <Badge variant="secondary" className="text-xs">
                            Fit: {persona.persona_fit_score.toFixed(0)}
                          </Badge>
                        )}
                        {persona.person_id && (
                          <span className="text-xs text-gray-400">ID: {persona.person_id}</span>
                        )}
                      </div>
                    </div>

                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeleteTarget(persona)}
                      disabled={isLoading}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {searchTerm && filteredPersonas.length === 0 && !isSearching && (
          <div className="text-center py-8 text-gray-500">
            <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No personas found matching "{searchTerm}"</p>
          </div>
        )}

        {!searchTerm && filteredPersonas.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Enter a name or role title to search for personas</p>
          </div>
        )}
      </CardContent>

      <AlertDialog open={deleteTarget !== null} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Confirm Deletion
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete this persona?
              
              {deleteTarget && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="font-semibold text-gray-900">
                    {deleteTarget.first_name} {deleteTarget.last_name}
                  </p>
                  {deleteTarget.role_title && (
                    <p className="text-sm text-gray-600 mt-1">{deleteTarget.role_title}</p>
                  )}
                  {deleteTarget.account_name && (
                    <p className="text-sm text-gray-600">
                      Account: {deleteTarget.account_name}
                    </p>
                  )}
                </div>
              )}
              
              <p className="mt-4 text-red-600 font-medium">
                This action cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {isLoading ? 'Deleting...' : 'Delete Persona'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}